import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Shield, TrendingDown, TrendingUp, AlertTriangle, RefreshCw, Lock, ChevronDown, ChevronUp, X, Activity, Flame } from 'lucide-react';

const WORKER_URL = 'https://tickertapapi.avetter90.workers.dev';

// ── Helpers ───────────────────────────────────────────────────────────────────
function isBuy(type = '') {
  const t = (type || '').toLowerCase().trim();
  if (!t) return false;
  // Insider Form 4 transaction codes (SEC): P=open-market purchase, A=grant/award (treat as acquisition)
  // Also common free-text: "purchase", "buy", "acquisition"
  if (t === 'p' || t === 'a' || t.startsWith('p-') || t.startsWith('a-')) return true;
  if (t.includes('purchase') || t.includes('buy') || t.includes('acqui')) return true;
  return false;
}

function daysAgo(dateStr) {
  if (!dateStr) return null;
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (diff === 0) return 'Today';
  if (diff === 1) return '1d ago';
  return `${diff}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function isCFO(trade) {
  const s = (trade.typeOfOwner || trade.reportingName || trade.title || '').toLowerCase();
  return s.includes('chief financial') || s.includes('cfo') || s.includes('c.f.o');
}

// Parse disclosed amount range strings like "$1,001 - $15,000" into midpoint estimate
function parseAmountRange(amt) {
  if (!amt || typeof amt !== 'string') return null;
  const nums = amt.match(/[\d,]+/g);
  if (!nums || nums.length === 0) return null;
  const parsed = nums.map(n => parseInt(n.replace(/,/g, ''), 10)).filter(n => !isNaN(n));
  if (parsed.length === 0) return null;
  if (parsed.length === 1) return parsed[0];
  return Math.floor((parsed[0] + parsed[parsed.length - 1]) / 2);
}

function formatDollars(n) {
  if (n == null) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toLocaleString()}`;
}

// ── Pro gate ──────────────────────────────────────────────────────────────────
function ProGate({ onUpgrade, loading }) {
  return (
    <div className="relative">
      <div className="space-y-2 select-none pointer-events-none" aria-hidden="true">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-4 flex items-center gap-4 opacity-25 blur-[2px]">
            <div className="w-16 h-5 bg-[#2a2a2a] rounded" />
            <div className="flex-1 h-4 bg-[#2a2a2a] rounded" />
            <div className="w-24 h-5 bg-[#2a2a2a] rounded" />
            <div className="w-16 h-4 bg-[#1a1a1a] rounded" />
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0a0a0a]/85 rounded-lg">
        <Lock className="w-8 h-8 text-amber-400 mb-3" />
        <p className="text-white font-semibold mb-1">Pro Feature</p>
        <p className="text-gray-400 text-sm mb-4 text-center max-w-xs">Government + insider intelligence. No competitor packages this for retail income investors.</p>
        <button onClick={onUpgrade} disabled={loading} className="px-5 py-2 bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-400 border border-cyan-500/30 rounded font-semibold text-sm transition-colors disabled:opacity-50">
          {loading ? 'Loading...' : 'Upgrade to Pro — $9/mo'}
        </button>
      </div>
    </div>
  );
}

// ── Congress row (with amount midpoint + optional click) ──────────────────────
function CongressRow({ trade, chamber, showTicker = false, onTickerClick }) {
  const buy = isBuy(trade.type || trade.transactionType || '');
  const name = trade.firstName
    ? `${trade.firstName} ${trade.lastName}`
    : trade.representative || trade.senator || trade.lastName || '—';
  const office = chamber === 'senate'
    ? (trade.office || 'Senate')
    : (trade.district || trade.office || 'House');
  const mid = parseAmountRange(trade.amount);
  const ticker = trade.ticker || trade.symbol || '';
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${buy ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
      {showTicker && (
        <button
          onClick={() => ticker && onTickerClick?.(ticker)}
          className={`font-mono font-bold text-white text-sm w-16 flex-shrink-0 text-left ${onTickerClick ? 'hover:text-cyan-400' : ''}`}
        >
          {ticker || '—'}
        </button>
      )}
      <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded flex-shrink-0 ${buy ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
        {buy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        {buy ? 'BUY' : 'SELL'}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium truncate">{name}</div>
        <div className="text-xs text-gray-500 truncate">{office}</div>
      </div>
      {trade.amount && (
        <div className="text-xs text-right flex-shrink-0 max-w-[160px]">
          <div className={`font-mono font-semibold ${buy ? 'text-emerald-400' : 'text-red-400'}`}>{mid != null ? `~${formatDollars(mid)}` : '—'}</div>
          <div className="text-gray-600 truncate">{trade.amount}</div>
        </div>
      )}
      <div className="text-xs text-gray-500 flex-shrink-0 w-20 text-right">
        <div>{daysAgo(trade.transactionDate || trade.dateRecieved)}</div>
        <div className="text-gray-600">{formatDate(trade.transactionDate || trade.dateRecieved)}</div>
      </div>
    </div>
  );
}

// ── Insider row ───────────────────────────────────────────────────────────────
function InsiderRow({ trade, showTicker = false, onTickerClick }) {
  const buy = isBuy(trade.transactionType || trade.type || '');
  const cfo = isCFO(trade);
  const ticker = trade.symbol || trade.ticker || '';
  const notional = (trade.securitiesTransacted && trade.price) ? Number(trade.securitiesTransacted) * Number(trade.price) : null;
  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-lg border ${buy ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
      {showTicker && (
        <button
          onClick={() => ticker && onTickerClick?.(ticker)}
          className={`font-mono font-bold text-white text-sm w-16 flex-shrink-0 text-left ${onTickerClick ? 'hover:text-cyan-400' : ''}`}
        >
          {ticker || '—'}
        </button>
      )}
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <span className={`flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded ${buy ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
          {buy ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {buy ? 'BUY' : 'SELL'}
        </span>
        {cfo && <span className="text-xs bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded font-bold border border-red-500/20">CFO</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white font-medium truncate">{trade.reportingName || '—'}</div>
        <div className="text-xs text-gray-500 truncate">{trade.typeOfOwner || '—'}</div>
      </div>
      {trade.securitiesTransacted && (
        <div className="text-xs text-right flex-shrink-0">
          <div className="text-gray-300">{Number(trade.securitiesTransacted).toLocaleString()} sh</div>
          {trade.price && <div className="text-gray-600">@ ${Number(trade.price).toFixed(2)}{notional ? ` · ${formatDollars(notional)}` : ''}</div>}
        </div>
      )}
      <div className="text-xs text-gray-500 flex-shrink-0 w-20 text-right">
        <div>{daysAgo(trade.transactionDate || trade.filingDate)}</div>
        <div className="text-gray-600">{formatDate(trade.transactionDate || trade.filingDate)}</div>
      </div>
    </div>
  );
}

// ── Collapsible section ───────────────────────────────────────────────────────
function Section({ title, subtitle, dot, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg overflow-hidden mb-3">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-[#111] transition-colors">
        <div className="flex items-center gap-3">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${dot}`} />
          <div className="text-left">
            <div className="text-sm font-semibold text-white">{title}</div>
            {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
          </div>
          {count > 0 && <span className="text-xs bg-[#1a1a1a] text-gray-400 px-2 py-0.5 rounded font-mono">{count}</span>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && <div className="px-4 pb-4 space-y-1.5">{children}</div>}
    </div>
  );
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function Stat({ label, value, color = 'text-white' }) {
  return (
    <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-4 flex-1">
      <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

// ── Loading skeleton ──────────────────────────────────────────────────────────
function Skeleton({ rows = 5 }) {
  return (
    <div className="space-y-2">
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-4 animate-pulse flex items-center gap-4">
          <div className="w-14 h-4 bg-[#1a1a1a] rounded" /><div className="w-12 h-5 bg-[#1a1a1a] rounded" /><div className="flex-1 h-4 bg-[#1a1a1a] rounded" /><div className="w-20 h-4 bg-[#1a1a1a] rounded" />
        </div>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TICKER DETAIL MODAL — price chart with trade markers overlaid
// ══════════════════════════════════════════════════════════════════════════════
function TickerModal({ ticker, session, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null); // pinned by click
  const [hovered, setHovered] = useState(null);   // transient from mouseover
  const hoverTimer = useRef(null);

  const scheduleClearHover = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHovered(null), 180);
  };
  const cancelClearHover = () => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null; }
  };
  const setHover = (v) => { cancelClearHover(); setHovered(v); };

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const r = await fetch(`${WORKER_URL}/api/intelligence/ticker-detail?ticker=${encodeURIComponent(ticker)}`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` }
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!cancelled) setData(d);
      } catch (e) { if (!cancelled) setError(e.message); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [ticker, session]);

  // Chart geometry + marker lookup
  const chart = useMemo(() => {
    if (!data || !data.prices || data.prices.length === 0) return null;
    const pts = [...data.prices].sort((a, b) => (a.date || '').localeCompare(b.date || ''));
    const dates = pts.map(p => p.date);
    const prices = pts.map(p => p.price ?? p.close ?? 0);
    const minP = Math.min(...prices);
    const maxP = Math.max(...prices);
    const padP = (maxP - minP) * 0.08 || 1;
    const yMin = minP - padP;
    const yMax = maxP + padP;
    const W = 1300, H = 520, PAD_L = 60, PAD_R = 30, PAD_T = 60, PAD_B = 40;
    const plotW = W - PAD_L - PAD_R;
    const plotH = H - PAD_T - PAD_B;
    const firstTs = new Date(dates[0]).getTime();
    const lastTs = new Date(dates[dates.length - 1]).getTime();
    const tsRange = Math.max(1, lastTs - firstTs);
    const xFor = (d) => PAD_L + ((new Date(d).getTime() - firstTs) / tsRange) * plotW;
    const yFor = (p) => PAD_T + (1 - (p - yMin) / (yMax - yMin)) * plotH;
    const pathD = pts.map((p, i) => {
      const x = xFor(p.date);
      const y = yFor(p.price ?? p.close ?? 0);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
    const areaD = `${pathD} L${xFor(dates[dates.length - 1])},${PAD_T + plotH} L${xFor(dates[0])},${PAD_T + plotH} Z`;
    const markerFor = (dateStr) => {
      if (!dateStr) return null;
      const ts = new Date(dateStr).getTime();
      if (ts < firstTs || ts > lastTs) return null;
      const x = xFor(dateStr);
      let closest = pts[0], bestDiff = Infinity;
      for (const p of pts) {
        const diff = Math.abs(new Date(p.date).getTime() - ts);
        if (diff < bestDiff) { bestDiff = diff; closest = p; }
      }
      return { x, y: yFor(closest.price ?? closest.close ?? 0) };
    };
    // Return the closing price on (or nearest to) a given date — used for Congress trades that have no price field
    const priceOnDate = (dateStr) => {
      if (!dateStr) return null;
      const ts = new Date(dateStr).getTime();
      let closest = pts[0], bestDiff = Infinity;
      for (const p of pts) {
        const diff = Math.abs(new Date(p.date).getTime() - ts);
        if (diff < bestDiff) { bestDiff = diff; closest = p; }
      }
      return closest.price ?? closest.close ?? null;
    };
    const firstPrice = prices[0];
    const lastPrice = prices[prices.length - 1];
    const totalChange = ((lastPrice - firstPrice) / firstPrice) * 100;
    return { pathD, areaD, W, H, PAD_L, PAD_R, PAD_T, PAD_B, plotW, plotH, yMin, yMax, xFor, yFor, markerFor, priceOnDate, firstPrice, lastPrice, totalChange, firstDate: dates[0], lastDate: dates[dates.length - 1] };
  }, [data]);

  // ── Trade matching ──────────────────────────────────────────────────────────
  // Group insider trades by person. For each, find buy→sell pairs (or any prior
  // counter-trade) — even when the counter-trade is OUTSIDE the 180d chart window.
  // A trade is "inWindow" if it lies inside the chart's date range (has a marker).
  const matching = useMemo(() => {
    if (!data || !chart) return { byTradeId: new Map(), peopleWithActivity: [] };
    const byPerson = new Map(); // key -> [{ trade, idx, buy, price, date, inWindow }]
    const keyOf = (t) => t.reportingCik || t.reportingName || '';
    (data.insider || []).forEach((t, idx) => {
      const key = keyOf(t);
      if (!key) return;
      const date = t.transactionDate || t.filingDate;
      if (!date) return;
      const buy = isBuy(t.transactionType || t.type || '');
      const price = t.price ? Number(t.price) : null;
      const inWindow = date >= chart.firstDate && date <= chart.lastDate;
      if (!byPerson.has(key)) byPerson.set(key, []);
      byPerson.get(key).push({ trade: t, idx, buy, price, date, inWindow });
    });

    // Sort each person's trades chronologically (oldest first)
    byPerson.forEach(arr => arr.sort((a, b) => a.date.localeCompare(b.date)));

    // Build lookup: for each trade, what's its matched counter-trade (if any)?
    // Matching strategy: for a SELL, find the MOST RECENT prior BUY by the same person
    // (to estimate % return). For a BUY, find the first subsequent SELL.
    const byTradeId = new Map();
    const peopleWithActivity = [];
    byPerson.forEach((arr, key) => {
      let hasBoth = arr.some(x => x.buy) && arr.some(x => !x.buy);
      if (hasBoth) peopleWithActivity.push({ key, name: arr[0].trade.reportingName, trades: arr });
      for (const entry of arr) {
        if (entry.buy) {
          // Find next sell by this person after this buy
          const nextSell = arr.find(x => !x.buy && x.date > entry.date && x.price != null);
          if (nextSell && entry.price != null) {
            byTradeId.set(entry.trade, { counter: nextSell.trade, type: 'buy-then-sell', entryPrice: entry.price, exitPrice: nextSell.price, returnPct: ((nextSell.price - entry.price) / entry.price) * 100, bothInWindow: entry.inWindow && nextSell.inWindow });
          }
        } else {
          // Find most recent prior buy by this person
          const priorBuys = arr.filter(x => x.buy && x.date < entry.date && x.price != null);
          if (priorBuys.length && entry.price != null) {
            const lastBuy = priorBuys[priorBuys.length - 1];
            byTradeId.set(entry.trade, { counter: lastBuy.trade, type: 'sell-vs-prior-buy', entryPrice: lastBuy.price, exitPrice: entry.price, returnPct: ((entry.price - lastBuy.price) / lastBuy.price) * 100, bothInWindow: entry.inWindow && lastBuy.inWindow });
          }
        }
      }
    });
    return { byTradeId, peopleWithActivity };
  }, [data, chart]);

  // ── Offset/cluster map ──────────────────────────────────────────────────────
  // For each calendar date with multiple markers across all trade types:
  //   - 2-3 trades on a date  → vertical stack (each marker visible & clickable)
  //   - 4+ trades on a date   → collapse into ONE numbered cluster badge
  //                             individual markers are hidden; the badge opens
  //                             the side panel in "list" mode showing all trades
  const CLUSTER_THRESHOLD = 4;
  const { offsetMap, clusters } = useMemo(() => {
    const offMap = new WeakMap();    // trade -> { dy, clusterId|null }
    const clusters = [];             // [{ id, date, trades: [{trade, kind}] }]
    if (!data) return { offsetMap: offMap, clusters };
    const byDate = new Map();
    const add = (arr, kind, dateKey) => {
      (arr || []).forEach(t => {
        const d = t[dateKey] || t.filingDate || t.disclosureDate;
        if (!d) return;
        if (!byDate.has(d)) byDate.set(d, []);
        byDate.get(d).push({ t, kind });
      });
    };
    add(data.senate,  'senate',  'transactionDate');
    add(data.house,   'house',   'transactionDate');
    add(data.insider, 'insider', 'transactionDate');
    let cid = 0;
    byDate.forEach((group, date) => {
      if (group.length >= CLUSTER_THRESHOLD) {
        const clusterId = `c${cid++}`;
        clusters.push({ id: clusterId, date, trades: group });
        group.forEach(({ t }) => offMap.set(t, { dy: 0, clusterId }));
      } else if (group.length > 1) {
        const step = 22;
        group.forEach(({ t }, i) => offMap.set(t, { dy: -i * step, clusterId: null }));
      } else {
        group.forEach(({ t }) => offMap.set(t, { dy: 0, clusterId: null }));
      }
    });
    return { offsetMap: offMap, clusters };
  }, [data]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="flex items-start gap-3 w-full justify-center" onClick={e => e.stopPropagation()}>
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl max-w-7xl w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1a1a1a] sticky top-0 bg-[#0a0a0a] z-10">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-cyan-400" />
            <div>
              <h3 className="text-xl font-bold text-white font-mono">{ticker}</h3>
              <p className="text-xs text-gray-500">180-day price with Senate, House, and Insider trade markers</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          {loading && <div className="flex items-center justify-center py-20 text-gray-500 text-sm"><RefreshCw className="w-4 h-4 animate-spin mr-2" />Loading price history and trades...</div>}
          {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{error}</div>}

          {!loading && !error && data && chart && (
            <>
              <div className="flex items-baseline gap-4 mb-4">
                <div className="text-3xl font-bold text-white font-mono">${chart.lastPrice.toFixed(2)}</div>
                <div className={`text-sm font-semibold ${chart.totalChange >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {chart.totalChange >= 0 ? '+' : ''}{chart.totalChange.toFixed(2)}% (180d)
                </div>
                <div className="text-xs text-gray-500 ml-auto">{formatDate(chart.firstDate)} → {formatDate(chart.lastDate)}</div>
              </div>

              <div className="bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-4 mb-4 overflow-x-auto"
                   onMouseEnter={cancelClearHover}
                   onMouseLeave={scheduleClearHover}>
                <svg viewBox={`0 0 ${chart.W} ${chart.H}`} className="w-full h-auto" style={{ maxHeight: '600px' }}>
                  <defs>
                    <linearGradient id="priceGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.25" />
                      <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  {[0, 0.25, 0.5, 0.75, 1].map((frac, i) => {
                    const y = chart.PAD_T + frac * chart.plotH;
                    const val = chart.yMax - frac * (chart.yMax - chart.yMin);
                    return (
                      <g key={i}>
                        <line x1={chart.PAD_L} x2={chart.W - chart.PAD_R} y1={y} y2={y} stroke="#1a1a1a" strokeWidth="1" />
                        <text x={chart.PAD_L - 6} y={y + 4} textAnchor="end" fill="#5a5a5a" fontSize="13" fontFamily="monospace">${val.toFixed(0)}</text>
                      </g>
                    );
                  })}
                  <path d={chart.areaD} fill="url(#priceGrad)" />
                  <path d={chart.pathD} fill="none" stroke="#22d3ee" strokeWidth="1.5" />
                  {data.senate.map((t, i) => {
                    const m0 = chart.markerFor(t.transactionDate || t.dateRecieved);
                    if (!m0) return null;
                    const off = offsetMap.get(t) || { dy: 0, clusterId: null };
                    if (off.clusterId) return null;
                    const m = { x: m0.x, y: m0.y + off.dy };
                    const buy = isBuy(t.type || t.transactionType || '');
                    const active = hovered || selected;
                    const isSel = active && active.kind === 'senate' && active.trade === t;
                    const fill = buy ? '#06b6d4' : '#f59e0b';
                    const stroke = buy ? '#22d3ee' : '#fbbf24';
                    const size = 11;
                    const glyph = buy ? '#052e1a' : '#2b1400';
                    return (
                      <g key={`s${i}`} style={{ cursor: 'pointer' }}
                         onMouseEnter={() => setHover({ kind: 'senate', trade: t })}
                         onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'senate', trade: t }); }}>
                        {off.dy !== 0 && <line x1={m0.x} y1={m0.y} x2={m.x} y2={m.y} stroke="#2a2a2a" strokeWidth="0.75" strokeDasharray="1,2" />}
                        {isSel && <circle cx={m.x} cy={m.y} r={size + 6} fill="none" stroke="#fff" strokeWidth="1.5" />}
                        <polygon points={`${m.x},${m.y - size} ${m.x + size},${m.y} ${m.x},${m.y + size} ${m.x - size},${m.y}`} fill={fill} stroke={stroke} strokeWidth="1.5" opacity="0.95" />
                        {buy
                          ? <polygon points={`${m.x},${m.y - 3.5} ${m.x - 3.5},${m.y + 2.5} ${m.x + 3.5},${m.y + 2.5}`} fill={glyph} />
                          : <polygon points={`${m.x},${m.y + 3.5} ${m.x - 3.5},${m.y - 2.5} ${m.x + 3.5},${m.y - 2.5}`} fill={glyph} />}
                      </g>
                    );
                  })}
                  {data.house.map((t, i) => {
                    const m0 = chart.markerFor(t.transactionDate || t.disclosureDate);
                    if (!m0) return null;
                    const off = offsetMap.get(t) || { dy: 0, clusterId: null };
                    if (off.clusterId) return null;
                    const m = { x: m0.x, y: m0.y + off.dy };
                    const buy = isBuy(t.type || t.transactionType || '');
                    const active = hovered || selected;
                    const isSel = active && active.kind === 'house' && active.trade === t;
                    const fill = buy ? '#10b981' : '#f97316';
                    const stroke = buy ? '#34d399' : '#fb923c';
                    const size = 9;
                    const glyph = buy ? '#052e1a' : '#2d1505';
                    return (
                      <g key={`h${i}`} style={{ cursor: 'pointer' }}
                         onMouseEnter={() => setHover({ kind: 'house', trade: t })}
                         onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'house', trade: t }); }}>
                        {off.dy !== 0 && <line x1={m0.x} y1={m0.y} x2={m.x} y2={m.y} stroke="#2a2a2a" strokeWidth="0.75" strokeDasharray="1,2" />}
                        {isSel && <circle cx={m.x} cy={m.y} r={size + 6} fill="none" stroke="#fff" strokeWidth="1.5" />}
                        <rect x={m.x - size} y={m.y - size} width={size * 2} height={size * 2} rx="2" fill={fill} stroke={stroke} strokeWidth="1.5" opacity="0.95" />
                        {buy
                          ? <polygon points={`${m.x},${m.y - 3.5} ${m.x - 3.5},${m.y + 2.5} ${m.x + 3.5},${m.y + 2.5}`} fill={glyph} />
                          : <polygon points={`${m.x},${m.y + 3.5} ${m.x - 3.5},${m.y - 2.5} ${m.x + 3.5},${m.y - 2.5}`} fill={glyph} />}
                      </g>
                    );
                  })}
                  {/* Matched-pair dotted lines (draw under markers) */}
                  {[...matching.byTradeId.entries()].map(([t, match], i) => {
                    if (!match.bothInWindow) return null;
                    const o1 = offsetMap.get(t) || { dy: 0, clusterId: null };
                    const o2 = offsetMap.get(match.counter) || { dy: 0, clusterId: null };
                    if (o1.clusterId || o2.clusterId) return null;
                    const m1 = chart.markerFor(t.transactionDate || t.filingDate);
                    const m2 = chart.markerFor(match.counter.transactionDate || match.counter.filingDate);
                    if (!m1 || !m2) return null;
                    const profitable = (match.returnPct >= 0) === !isBuy(t.transactionType || t.type || '');
                    if (m1.x > m2.x) return null;
                    return <line key={`match${i}`} x1={m1.x} y1={m1.y + o1.dy} x2={m2.x} y2={m2.y + o2.dy}
                                 stroke={profitable ? '#fbbf24' : '#64748b'} strokeWidth="1" strokeDasharray="4,3" opacity="0.55" />;
                  })}

                  {data.insider.map((t, i) => {
                    const m0 = chart.markerFor(t.transactionDate || t.filingDate);
                    if (!m0) return null;
                    const off = offsetMap.get(t) || { dy: 0, clusterId: null };
                    if (off.clusterId) return null;
                    const m = { x: m0.x, y: m0.y + off.dy };
                    const buy = isBuy(t.transactionType || t.type || '');
                    const cfo = isCFO(t);
                    const active = hovered || selected;
                    const isSel = active && active.kind === 'insider' && active.trade === t;
                    const size = cfo ? 12 : 9;
                    const fill = buy ? '#22c55e' : cfo ? '#ef4444' : '#dc2626';
                    const stroke = buy ? '#4ade80' : cfo ? '#fca5a5' : '#f87171';
                    const glyph = buy ? '#052e1a' : '#2a0505';
                    const match = matching.byTradeId.get(t);
                    let pairHighlight = false;
                    if (active && active.kind === 'insider') {
                      const activeMatch = matching.byTradeId.get(active.trade);
                      if (activeMatch && activeMatch.counter === t) pairHighlight = true;
                    }
                    const arrowScale = cfo ? 1.25 : 1;
                    return (
                      <g key={`i${i}`} style={{ cursor: 'pointer' }}
                         onMouseEnter={() => setHover({ kind: 'insider', trade: t })}
                         onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'insider', trade: t }); }}>
                        {off.dy !== 0 && <line x1={m0.x} y1={m0.y} x2={m.x} y2={m.y} stroke="#2a2a2a" strokeWidth="0.75" strokeDasharray="1,2" />}
                        {(isSel || pairHighlight) && <circle cx={m.x} cy={m.y} r={size + 6} fill="none" stroke={pairHighlight && !isSel ? '#fbbf24' : '#fff'} strokeWidth="1.5" strokeDasharray={pairHighlight && !isSel ? '3,2' : undefined} />}
                        <circle cx={m.x} cy={m.y} r={size} fill={fill} stroke={stroke} strokeWidth="1.5" opacity="0.95" />
                        {buy
                          ? <polygon points={`${m.x},${m.y - 4 * arrowScale} ${m.x - 4 * arrowScale},${m.y + 3 * arrowScale} ${m.x + 4 * arrowScale},${m.y + 3 * arrowScale}`} fill={glyph} />
                          : <polygon points={`${m.x},${m.y + 4 * arrowScale} ${m.x - 4 * arrowScale},${m.y - 3 * arrowScale} ${m.x + 4 * arrowScale},${m.y - 3 * arrowScale}`} fill={glyph} />}
                      </g>
                    );
                  })}

                  {/* CLUSTER BADGES — render one numbered circle per crowded date */}
                  {clusters.map((cluster) => {
                    const m0 = chart.markerFor(cluster.date);
                    if (!m0) return null;
                    // Stats for the badge
                    let sells = 0, buys = 0, hasCfo = false;
                    cluster.trades.forEach(({ t, kind }) => {
                      const buy = kind === 'insider'
                        ? isBuy(t.transactionType || t.type || '')
                        : isBuy(t.type || t.transactionType || '');
                      if (buy) buys++; else sells++;
                      if (kind === 'insider' && isCFO(t)) hasCfo = true;
                    });
                    const allSells = buys === 0;
                    const allBuys = sells === 0;
                    const fill = hasCfo ? '#ef4444' : allSells ? '#dc2626' : allBuys ? '#22c55e' : '#a78bfa'; // mixed = purple
                    const stroke = hasCfo ? '#fecaca' : allSells ? '#f87171' : allBuys ? '#86efac' : '#c4b5fd';
                    const r = 16; // bigger than any individual marker so it stands out
                    const yOff = -28; // sit above the price point with breathing room
                    const cx = m0.x;
                    const cy = m0.y + yOff;
                    const active = hovered || selected;
                    const isSel = active && active.kind === 'cluster' && active.cluster.id === cluster.id;
                    return (
                      <g key={cluster.id} style={{ cursor: 'pointer' }}
                         onMouseEnter={() => setHover({ kind: 'cluster', cluster })}
                         onClick={(e) => { e.stopPropagation(); setSelected({ kind: 'cluster', cluster }); }}>
                        {/* tether to price */}
                        <line x1={cx} y1={m0.y} x2={cx} y2={cy + r} stroke="#3a3a3a" strokeWidth="1" strokeDasharray="2,2" />
                        {isSel && <circle cx={cx} cy={cy} r={r + 6} fill="none" stroke="#fff" strokeWidth="1.5" />}
                        <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth="2" opacity="0.97" />
                        <text x={cx} y={cy + 5} textAnchor="middle" fill="#fff" fontSize="14" fontWeight="700" fontFamily="system-ui, -apple-system, sans-serif" pointerEvents="none">{cluster.trades.length}</text>
                        {/* mini sells/buys indicator under the badge */}
                        <text x={cx} y={cy + r + 12} textAnchor="middle" fill="#9ca3af" fontSize="9" fontFamily="monospace" pointerEvents="none">
                          {sells > 0 ? `${sells}S` : ''}{sells > 0 && buys > 0 ? ' · ' : ''}{buys > 0 ? `${buys}B` : ''}
                        </text>
                      </g>
                    );
                  })}
                </svg>
                <div className="flex items-center gap-5 mt-3 text-xs text-gray-500 flex-wrap">
                  <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-cyan-500 rotate-45" />Senate</div>
                  <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-orange-500 rounded-sm" />House</div>
                  <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-red-600 rounded-full" />Insider sell</div>
                  <div className="flex items-center gap-1.5"><span className="inline-block w-3.5 h-3.5 bg-red-500 rounded-full ring-1 ring-red-300" />CFO sell</div>
                  <div className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-green-500 rounded-full" />Insider buy</div>
                  <div className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-4 h-4 bg-purple-500 rounded-full text-[8px] text-white font-bold">N</span>cluster (4+)</div>
                  <div className="flex items-center gap-1.5 text-gray-500">▲=buy<span className="text-gray-700">·</span>▼=sell</div>
                  <div className="flex items-center gap-1.5"><span className="inline-block w-4 border-t border-dashed border-amber-400" />matched</div>
                  <span className="text-gray-600 ml-auto">Hover preview · click to pin</span>
                </div>
              </div>

              <div className="flex gap-3 text-xs">
                <div className="flex-1 bg-[#0f0f0f] border border-[#1a1a1a] rounded px-3 py-2"><span className="text-gray-500">Senate: </span><span className="text-white font-semibold">{data.senate.length}</span></div>
                <div className="flex-1 bg-[#0f0f0f] border border-[#1a1a1a] rounded px-3 py-2"><span className="text-gray-500">House: </span><span className="text-white font-semibold">{data.house.length}</span></div>
                <div className="flex-1 bg-[#0f0f0f] border border-[#1a1a1a] rounded px-3 py-2"><span className="text-gray-500">Insider: </span><span className="text-white font-semibold">{data.insider.length}</span></div>
                <div className="flex-1 bg-[#0f0f0f] border border-[#1a1a1a] rounded px-3 py-2"><span className="text-gray-500">CFO: </span><span className="text-red-400 font-semibold">{data.insider.filter(isCFO).length}</span></div>
              </div>
            </>
          )}

          {!loading && !error && data && !chart && (
            <div className="text-center py-12 text-gray-600 text-sm">No price history returned for {ticker}.</div>
          )}
        </div>
        </div>

        {/* Side panel — switches between cluster list and trade detail */}
        {(hovered || selected) && (
          (hovered || selected).kind === 'cluster'
            ? <ClusterListPanel cluster={(hovered || selected).cluster} chart={chart} lastPrice={chart?.lastPrice} matching={matching} pinned={!!selected && !hovered} onPick={(kind, trade) => setSelected({ kind, trade })} onClose={() => { setSelected(null); setHovered(null); }} />
            : <TradeDetailPanel selected={hovered || selected} chart={chart} lastPrice={chart?.lastPrice} pinned={!!selected && !hovered} matching={matching} onClose={() => { setSelected(null); setHovered(null); }} />
        )}
      </div>
    </div>
  );
}

// ── Trade detail slide-in panel ───────────────────────────────────────────────
function TradeDetailPanel({ selected, chart, lastPrice, pinned, matching, onClose }) {
  if (!selected) return null;
  const { kind, trade } = selected;
  const buy = kind === 'insider'
    ? isBuy(trade.transactionType || trade.type || '')
    : isBuy(trade.type || trade.transactionType || '');
  const dateStr = trade.transactionDate || trade.dateRecieved || trade.disclosureDate || trade.filingDate;
  const cfo = kind === 'insider' && isCFO(trade);

  // Header colors
  const accentBg = buy ? 'bg-emerald-500/20' : cfo ? 'bg-red-500/25' : 'bg-red-500/15';
  const accentText = buy ? 'text-emerald-300' : cfo ? 'text-red-300' : 'text-red-300';
  const accentBorder = buy ? 'border-emerald-500/30' : cfo ? 'border-red-500/40' : 'border-red-500/25';

  // Name / office
  const name = kind === 'insider'
    ? (trade.reportingName || '—')
    : (trade.firstName ? `${trade.firstName} ${trade.lastName}` : trade.representative || trade.senator || trade.lastName || '—');
  const subline = kind === 'insider'
    ? (trade.typeOfOwner || '—')
    : kind === 'senate'
      ? (trade.office || 'U.S. Senate')
      : (trade.district || trade.office || 'U.S. House');

  // Amount and return math
  let exactAmount = null; // number when exact (insider)
  let rangeLabel = null;  // string when only a range (congress)
  let midpoint = null;
  let tradePrice = null;  // what was paid (insider: actual; congress: closing price on trade date)
  let shares = null;
  let returnPct = null;

  if (kind === 'insider') {
    shares = trade.securitiesTransacted ? Number(trade.securitiesTransacted) : null;
    tradePrice = trade.price ? Number(trade.price) : null;
    if (shares && tradePrice) exactAmount = shares * tradePrice;
    if (tradePrice && lastPrice) returnPct = ((lastPrice - tradePrice) / tradePrice) * 100;
  } else {
    rangeLabel = trade.amount || null;
    midpoint = parseAmountRange(trade.amount);
    if (chart?.priceOnDate) tradePrice = chart.priceOnDate(dateStr);
    if (tradePrice && lastPrice) returnPct = ((lastPrice - tradePrice) / tradePrice) * 100;
  }

  const returnColor = returnPct == null ? 'text-gray-400' : returnPct >= 0 ? 'text-emerald-400' : 'text-red-400';

  return (
    <div className="w-[340px] max-h-[90vh] bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl shadow-2xl flex flex-col overflow-hidden flex-shrink-0">

      <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a1a1a]">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${accentBg} ${accentText} border ${accentBorder}`}>
            {kind.toUpperCase()} · {buy ? 'BUY' : 'SELL'}
          </span>
          {cfo && <span className="text-xs bg-red-500/20 text-red-300 px-1.5 py-0.5 rounded font-bold border border-red-500/30">CFO</span>}
          {pinned && <span className="text-[10px] text-cyan-400 border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 rounded font-semibold">PINNED</span>}
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white" title={pinned ? 'Unpin' : 'Close'}><X className="w-4 h-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-sm">
        {/* Who */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Who</div>
          <div className="text-white font-semibold">{name}</div>
          <div className="text-xs text-gray-500">{subline}</div>
        </div>

        {/* When */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">When</div>
          <div className="text-white">{formatDate(dateStr)}</div>
          <div className="text-xs text-gray-500">{daysAgo(dateStr)}</div>
          {kind !== 'insider' && (trade.disclosureDate && trade.disclosureDate !== dateStr) && (
            <div className="text-xs text-gray-600 mt-0.5">Disclosed {formatDate(trade.disclosureDate)} (lag: {Math.floor((new Date(trade.disclosureDate).getTime() - new Date(dateStr).getTime()) / (86400000))}d)</div>
          )}
        </div>

        {/* Amount */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Amount</div>
          {kind === 'insider' ? (
            <>
              <div className="text-white font-mono font-semibold text-lg">{exactAmount != null ? formatDollars(exactAmount) : '—'}</div>
              <div className="text-xs text-gray-500">{shares ? `${shares.toLocaleString()} sh` : ''}{shares && tradePrice ? ' · ' : ''}{tradePrice ? `@ $${tradePrice.toFixed(2)}` : ''}</div>
              <div className="text-[10px] text-gray-600 mt-1">Exact amount from SEC Form 4 filing</div>
            </>
          ) : (
            <>
              <div className="text-white font-mono font-semibold text-lg">{midpoint != null ? `~${formatDollars(midpoint)}` : '—'}<span className="text-xs text-gray-500 font-normal ml-1.5">midpoint</span></div>
              <div className="text-xs text-gray-500">Range: {rangeLabel || '—'}</div>
              <div className="text-[10px] text-gray-600 mt-1">Per the STOCK Act, {kind === 'senate' ? 'senators' : 'representatives'} disclose only dollar ranges, not exact amounts</div>
            </>
          )}
        </div>

        {/* Return since trade */}
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Since the trade</div>
          {tradePrice && lastPrice ? (
            <>
              <div className={`font-mono font-semibold text-lg ${returnColor}`}>
                {returnPct >= 0 ? '+' : ''}{returnPct.toFixed(2)}%
              </div>
              <div className="text-xs text-gray-500">
                ${tradePrice.toFixed(2)} <span className="text-gray-600">({kind === 'insider' ? 'filing price' : 'close on trade date'})</span> → ${lastPrice.toFixed(2)} <span className="text-gray-600">(current)</span>
              </div>
              {!buy && returnPct < 0 && <div className="text-[11px] text-red-400 mt-1.5">✓ Sell was profitable — stock is down since</div>}
              {!buy && returnPct > 0 && <div className="text-[11px] text-gray-500 mt-1.5">Sold too early — stock is up since</div>}
              {buy && returnPct > 0 && <div className="text-[11px] text-emerald-400 mt-1.5">✓ Buy has appreciated since</div>}
              {buy && returnPct < 0 && <div className="text-[11px] text-gray-500 mt-1.5">Buy underwater since trade</div>}
            </>
          ) : (
            <div className="text-xs text-gray-600">Insufficient price data to compute return</div>
          )}
        </div>

        {/* Matched counter-trade (insider only) */}
        {kind === 'insider' && matching && matching.byTradeId.get(trade) && (() => {
          const match = matching.byTradeId.get(trade);
          const counter = match.counter;
          const counterBuy = isBuy(counter.transactionType || counter.type || '');
          const pairProfitable = match.returnPct >= 0;
          return (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-3">
              <div className="text-xs text-amber-400 uppercase tracking-wider mb-1.5 font-semibold flex items-center gap-1.5">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                Matched to prior {counterBuy ? 'buy' : 'sell'}
              </div>
              <div className="text-xs text-gray-400 mb-1">Same insider: <span className="text-white font-medium">{counter.reportingName}</span></div>
              <div className="text-xs text-gray-500">
                {counterBuy ? 'Bought' : 'Sold'} {counter.securitiesTransacted ? `${Number(counter.securitiesTransacted).toLocaleString()} sh` : ''} @ ${match.entryPrice.toFixed(2)} on {formatDate(counter.transactionDate || counter.filingDate)}
              </div>
              <div className="mt-2 flex items-baseline gap-2">
                <span className={`font-mono font-bold text-base ${pairProfitable ? 'text-emerald-400' : 'text-red-400'}`}>
                  {pairProfitable ? '+' : ''}{match.returnPct.toFixed(2)}%
                </span>
                <span className="text-[11px] text-gray-500">
                  {match.type === 'sell-vs-prior-buy' ? 'realized on this sell' : 'if sold at current sell price'}
                </span>
              </div>
              {!match.bothInWindow && <div className="text-[10px] text-gray-600 mt-1.5">Counter-trade is outside the 180d chart window</div>}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── Cluster list panel — shown when user picks a date with 4+ trades ──────────
function ClusterListPanel({ cluster, chart, lastPrice, matching, pinned, onPick, onClose }) {
  if (!cluster) return null;
  // Group cluster trades by kind for clean section headers
  const senate  = cluster.trades.filter(x => x.kind === 'senate').map(x => x.t);
  const house   = cluster.trades.filter(x => x.kind === 'house').map(x => x.t);
  const insider = cluster.trades.filter(x => x.kind === 'insider').map(x => x.t);
  const sells = cluster.trades.filter(x => {
    const buy = x.kind === 'insider' ? isBuy(x.t.transactionType || x.t.type || '') : isBuy(x.t.type || x.t.transactionType || '');
    return !buy;
  }).length;
  const buys = cluster.trades.length - sells;
  const cfoCount = insider.filter(isCFO).length;

  const Row = ({ kind, trade }) => {
    const buy = kind === 'insider' ? isBuy(trade.transactionType || trade.type || '') : isBuy(trade.type || trade.transactionType || '');
    const cfo = kind === 'insider' && isCFO(trade);
    const name = kind === 'insider' ? (trade.reportingName || '—')
               : (trade.firstName ? `${trade.firstName} ${trade.lastName}` : trade.representative || trade.lastName || '—');
    const sub  = kind === 'insider' ? (trade.typeOfOwner || '—')
               : kind === 'senate' ? (trade.office || 'Senate') : (trade.district || 'House');
    const amt  = kind === 'insider' && trade.securitiesTransacted && trade.price
               ? formatDollars(Number(trade.securitiesTransacted) * Number(trade.price))
               : (trade.amount && parseAmountRange(trade.amount) != null ? `~${formatDollars(parseAmountRange(trade.amount))}` : '—');
    return (
      <button onClick={() => onPick(kind, trade)}
              className={`w-full text-left px-3 py-2 rounded border text-xs hover:border-[#3a3a3a] transition-colors ${buy ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-red-500/5 border-red-500/15'}`}>
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${buy ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}>{buy ? 'BUY' : 'SELL'}</span>
          {cfo && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-red-500/25 text-red-200 border border-red-500/30">CFO</span>}
          <span className="text-white font-medium truncate flex-1">{name}</span>
          <span className={`font-mono font-semibold ${buy ? 'text-emerald-300' : 'text-red-300'}`}>{amt}</span>
        </div>
        <div className="text-[10px] text-gray-500 truncate">{sub}</div>
      </button>
    );
  };

  return (
    <div className="w-[360px] max-h-[92vh] bg-[#0a0a0a] border border-[#1a1a1a] rounded-xl shadow-2xl flex flex-col overflow-hidden flex-shrink-0">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a1a1a]">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs font-semibold px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">CLUSTER · {cluster.trades.length} trades</span>
          {pinned && <span className="text-[10px] text-cyan-400 border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 rounded font-semibold">PINNED</span>}
        </div>
        <button onClick={onClose} className="text-gray-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>

      <div className="px-5 py-3 border-b border-[#1a1a1a]">
        <div className="text-xs text-gray-500 uppercase tracking-wider">{formatDate(cluster.date)}</div>
        <div className="flex items-center gap-3 mt-1.5 text-xs">
          {sells > 0 && <span className="text-red-400 font-semibold">{sells} sell{sells > 1 ? 's' : ''}</span>}
          {buys  > 0 && <span className="text-emerald-400 font-semibold">{buys} buy{buys > 1 ? 's' : ''}</span>}
          {cfoCount > 0 && <span className="text-red-300 font-bold">{cfoCount} CFO</span>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {senate.length > 0 && (
          <div>
            <div className="text-[10px] text-cyan-400 uppercase tracking-wider mb-1.5 font-semibold">Senate ({senate.length})</div>
            <div className="space-y-1.5">{senate.map((t, i) => <Row key={`cs${i}`} kind="senate" trade={t} />)}</div>
          </div>
        )}
        {house.length > 0 && (
          <div>
            <div className="text-[10px] text-orange-400 uppercase tracking-wider mb-1.5 font-semibold">House ({house.length})</div>
            <div className="space-y-1.5">{house.map((t, i) => <Row key={`ch${i}`} kind="house" trade={t} />)}</div>
          </div>
        )}
        {insider.length > 0 && (
          <div>
            <div className="text-[10px] text-red-400 uppercase tracking-wider mb-1.5 font-semibold">Insider ({insider.length})</div>
            <div className="space-y-1.5">{insider.map((t, i) => <Row key={`ci${i}`} kind="insider" trade={t} />)}</div>
          </div>
        )}
      </div>

      <div className="px-5 py-2.5 border-t border-[#1a1a1a] text-[10px] text-gray-600">Click any trade for details</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MY HOLDINGS TAB
// ══════════════════════════════════════════════════════════════════════════════
function MyHoldings({ session, subscription, positions, onUpgrade, stripeLoading, onTickerClick }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);
  const [senateTrades, setSenateTrades] = useState([]);
  const [houseTrades, setHouseTrades] = useState([]);
  const [insiderTrades, setInsiderTrades] = useState([]);
  const [velocity, setVelocity] = useState({});
  const isPro = ['pro', 'premium', 'founder'].includes(subscription.tier);
  const tickers = positions.map(p => p.ticker);

  const fetchAll = useCallback(async () => {
    if (!isPro || !session || tickers.length === 0) return;
    setLoading(true); setError(null);
    try {
      const tickerStr = tickers.join(',');
      const h = { 'Authorization': `Bearer ${session.access_token}` };
      const [sr, hr, ir, vr] = await Promise.all([
        fetch(`${WORKER_URL}/api/intelligence/senate?tickers=${tickerStr}`, { headers: h }),
        fetch(`${WORKER_URL}/api/intelligence/house?tickers=${tickerStr}`, { headers: h }),
        fetch(`${WORKER_URL}/api/intelligence/insider?tickers=${tickerStr}`, { headers: h }),
        fetch(`${WORKER_URL}/api/intelligence/velocity?tickers=${tickerStr}`, { headers: h }),
      ]);
      if (sr.ok) { const d = await sr.json(); setSenateTrades(d.trades || []); }
      if (hr.ok) { const d = await hr.json(); setHouseTrades(d.trades || []); }
      if (ir.ok) { const d = await ir.json(); setInsiderTrades(d.trades || []); }
      if (vr.ok) { const d = await vr.json(); setVelocity(d.clusters || {}); }
      setLastFetched(new Date());
    } catch (e) { setError('Failed to load. Check worker logs.'); }
    finally { setLoading(false); }
  }, [isPro, session, tickers.join(',')]); // eslint-disable-line

  useEffect(() => { if (isPro && tickers.length > 0) fetchAll(); }, [isPro, tickers.length]); // eslint-disable-line

  if (!isPro) return <ProGate onUpgrade={onUpgrade} loading={stripeLoading} />;

  const senateSells  = senateTrades.filter(t => !isBuy(t.type || t.transactionType || ''));
  const senateBuys   = senateTrades.filter(t =>  isBuy(t.type || t.transactionType || ''));
  const houseSells   = houseTrades.filter(t =>  !isBuy(t.type || ''));
  const houseBuys    = houseTrades.filter(t =>   isBuy(t.type || ''));
  const insiderSells = insiderTrades.filter(t => !isBuy(t.transactionType || t.type || ''));
  const insiderBuys  = insiderTrades.filter(t =>  isBuy(t.transactionType || t.type || ''));
  const cfoSells     = insiderSells.filter(isCFO);
  const otherSells   = insiderSells.filter(t => !isCFO(t));
  const clusterTickers = Object.entries(velocity).filter(([, v]) => v.isCluster).map(([k]) => k);
  const totalSells   = senateSells.length + houseSells.length + insiderSells.length;
  const riskLevel    = clusterTickers.length > 0 || cfoSells.length > 0 ? 'HIGH' : totalSells >= 5 ? 'HIGH' : totalSells >= 2 ? 'ELEVATED' : 'NORMAL';
  const riskColor    = riskLevel === 'HIGH' ? 'text-red-400' : riskLevel === 'ELEVATED' ? 'text-yellow-400' : 'text-emerald-400';

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-gray-600">{tickers.length} positions monitored · 45d Congressional / 21d Insider / 14d Velocity</p>
        <div className="flex items-center gap-3">
          {lastFetched && <span className="text-xs text-gray-600">Updated {lastFetched.toLocaleTimeString()}</span>}
          <button onClick={fetchAll} disabled={loading} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white px-3 py-1.5 bg-[#0f0f0f] border border-[#1a1a1a] rounded hover:border-[#2a2a2a] transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400 text-sm"><AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}</div>}

      {!loading && clusterTickers.length > 0 && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 mb-4 flex items-start gap-3">
          <Flame className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-red-300">Insider Sell Cluster Detected</div>
            <div className="text-xs text-gray-400 mt-1">3+ distinct insiders selling in the last 14 days on:{' '}
              {clusterTickers.map((t, i) => {
                const v = velocity[t];
                return (
                  <span key={t}>
                    <button onClick={() => onTickerClick?.(t)} className="font-mono font-bold text-red-400 hover:text-red-300">{t}</button>
                    <span className="text-gray-500"> ({v.distinctInsiders} insiders{v.cfoCount > 0 ? `, ${v.cfoCount} CFO` : ''})</span>
                    {i < clusterTickers.length - 1 && <span className="text-gray-600">, </span>}
                  </span>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!loading && (
        <div className="flex gap-3 mb-5 flex-wrap">
          <Stat label="Senate Sells"  value={senateSells.length}  color={senateSells.length  > 0 ? 'text-red-400'     : 'text-emerald-400'} />
          <Stat label="Senate Buys"   value={senateBuys.length}   color={senateBuys.length   > 0 ? 'text-emerald-400' : 'text-gray-400'} />
          <Stat label="Insider Sells" value={insiderSells.length} color={insiderSells.length > 0 ? 'text-red-400'     : 'text-emerald-400'} />
          <Stat label="CFO Sells"     value={cfoSells.length}     color={cfoSells.length     > 0 ? 'text-red-400'     : 'text-gray-400'} />
          <Stat label="Clusters"      value={clusterTickers.length} color={clusterTickers.length > 0 ? 'text-red-400' : 'text-gray-400'} />
          <Stat label="Portfolio Risk" value={riskLevel} color={riskColor} />
        </div>
      )}

      {loading && <Skeleton rows={5} />}

      {!loading && (
        <Section title="Senate Trades" subtitle="45-day lookback" dot="bg-cyan-400" count={senateTrades.length} defaultOpen={true}>
          {senateTrades.length === 0
            ? <div className="text-center py-6 text-gray-600 text-sm">No Senate activity on your holdings in the last 45 days</div>
            : <>
                {senateSells.length > 0 && <><div className="text-xs text-red-400 font-semibold uppercase tracking-wider pt-1 pb-1.5 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Sales ({senateSells.length})</div>{senateSells.map((t, i) => <CongressRow key={i} trade={t} chamber="senate" showTicker={true} onTickerClick={onTickerClick} />)}</>}
                {senateBuys.length  > 0 && <><div className="text-xs text-emerald-400 font-semibold uppercase tracking-wider pt-3 pb-1.5 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Purchases ({senateBuys.length})</div>{senateBuys.map((t, i) => <CongressRow key={i} trade={t} chamber="senate" showTicker={true} onTickerClick={onTickerClick} />)}</>}
              </>
          }
        </Section>
      )}

      {!loading && (
        <Section title="House Trades" subtitle="45-day lookback · Longer disclosure lag" dot="bg-purple-400" count={houseTrades.length} defaultOpen={houseTrades.length > 0}>
          {houseTrades.length === 0
            ? <div className="text-center py-6 text-gray-600 text-sm">No House activity on your holdings in the last 45 days</div>
            : <>
                {houseSells.length > 0 && <><div className="text-xs text-red-400 font-semibold uppercase tracking-wider pt-1 pb-1.5 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Sales ({houseSells.length})</div>{houseSells.map((t, i) => <CongressRow key={i} trade={t} chamber="house" showTicker={true} onTickerClick={onTickerClick} />)}</>}
                {houseBuys.length  > 0 && <><div className="text-xs text-emerald-400 font-semibold uppercase tracking-wider pt-3 pb-1.5 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Purchases ({houseBuys.length})</div>{houseBuys.map((t, i) => <CongressRow key={i} trade={t} chamber="house" showTicker={true} onTickerClick={onTickerClick} />)}</>}
              </>
          }
        </Section>
      )}

      {!loading && (
        <Section title="Insider Form 4" subtitle="21-day lookback · CFO sells flagged" dot="bg-amber-400" count={insiderTrades.length} defaultOpen={insiderTrades.length > 0}>
          {insiderTrades.length === 0
            ? <div className="text-center py-6 text-gray-600 text-sm">No insider activity on your holdings in the last 21 days</div>
            : <>
                {cfoSells.length    > 0 && <><div className="text-xs text-red-400 font-semibold uppercase tracking-wider pt-1 pb-1.5 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />CFO Sales — Highest Weight ({cfoSells.length})</div>{cfoSells.map((t, i) => <InsiderRow key={i} trade={t} showTicker={true} onTickerClick={onTickerClick} />)}</>}
                {otherSells.length  > 0 && <><div className="text-xs text-orange-400 font-semibold uppercase tracking-wider pt-3 pb-1.5 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Other Insider Sales ({otherSells.length})</div>{otherSells.map((t, i) => <InsiderRow key={i} trade={t} showTicker={true} onTickerClick={onTickerClick} />)}</>}
                {insiderBuys.length > 0 && <><div className="text-xs text-emerald-400 font-semibold uppercase tracking-wider pt-3 pb-1.5 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Purchases ({insiderBuys.length})</div>{insiderBuys.map((t, i) => <InsiderRow key={i} trade={t} showTicker={true} onTickerClick={onTickerClick} />)}</>}
              </>
          }
        </Section>
      )}

      {!loading && (
        <div className="mt-4 bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-4">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Signal Weight Reference</div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5 text-xs text-gray-500">
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" /><span>Senator cluster sell (3+) → <span className="text-red-400 font-semibold">CRITICAL</span></span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" /><span>CFO insider sell → <span className="text-red-400 font-semibold">CRITICAL</span></span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" /><span>Single senator sell → <span className="text-orange-400 font-semibold">HIGH</span></span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0" /><span>Insider cluster sell (3+) → <span className="text-orange-400 font-semibold">HIGH</span></span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" /><span>Senator/insider buy → dampens risk score</span></div>
            <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" /><span>Click any ticker → price chart + trade markers</span></div>
          </div>
          <div className="mt-2 pt-2 border-t border-[#1a1a1a] text-xs text-gray-600">Disclosure lag: Senate ~30–45d · House ~45–60d</div>
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MARKET FEED TAB
// ══════════════════════════════════════════════════════════════════════════════
function MarketFeed({ session, subscription, onUpgrade, stripeLoading, onTickerClick }) {
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState(null);
  const [page, setPage]         = useState(0);
  const [feedType, setFeedType] = useState('all');
  const [senate, setSenate]     = useState([]);
  const [house, setHouse]       = useState([]);
  const [insider, setInsider]   = useState([]);
  const [filterDir, setFilterDir] = useState('all');
  const [search, setSearch]     = useState('');
  const isPro = ['pro', 'premium', 'founder'].includes(subscription.tier);

  const fetchFeed = useCallback(async (pg, type) => {
    if (!isPro || !session) return;
    setLoading(true); setError(null);
    try {
      const h = { 'Authorization': `Bearer ${session.access_token}` };
      const r = await fetch(`${WORKER_URL}/api/intelligence/feed?page=${pg}&type=${type}`, { headers: h });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setSenate(d.senate   || []);
      setHouse(d.house     || []);
      setInsider(d.insider || []);
    } catch (e) { setError('Failed to load feed.'); }
    finally { setLoading(false); }
  }, [isPro, session]); // eslint-disable-line

  useEffect(() => { if (isPro) fetchFeed(0, feedType); }, [isPro, feedType]); // eslint-disable-line

  if (!isPro) return <ProGate onUpgrade={onUpgrade} loading={stripeLoading} />;

  const filterTrade = (t, kind) => {
    const buy = kind === 'congress'
      ? isBuy(t.type || t.transactionType || '')
      : isBuy(t.transactionType || t.type || '');
    if (filterDir === 'sell' && buy)  return false;
    if (filterDir === 'buy'  && !buy) return false;
    if (search) {
      const ticker = (t.ticker || t.symbol || '').toUpperCase();
      const name   = kind === 'congress'
        ? `${t.firstName || ''} ${t.lastName || ''} ${t.representative || ''}`.toUpperCase()
        : (t.reportingName || '').toUpperCase();
      if (!ticker.includes(search) && !name.includes(search)) return false;
    }
    return true;
  };

  const fSenate  = senate.filter(t  => filterTrade(t, 'congress'));
  const fHouse   = house.filter(t   => filterTrade(t, 'congress'));
  const fInsider = insider.filter(t => filterTrade(t, 'insider'));

  const changePage = (p) => { setPage(p); fetchFeed(p, feedType); };

  return (
    <>
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-0.5 gap-0.5">
          {[['all','All'],['senate','Senate'],['house','House'],['insider','Insider']].map(([id, label]) => (
            <button key={id} onClick={() => { setFeedType(id); setPage(0); }} className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${feedType === id ? 'bg-[#2a2a2a] text-white' : 'text-gray-500 hover:text-gray-300'}`}>{label}</button>
          ))}
        </div>
        <div className="flex bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-0.5 gap-0.5">
          {[['all','All'],['sell','Sells'],['buy','Buys']].map(([id, label]) => (
            <button key={id} onClick={() => setFilterDir(id)} className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${filterDir === id ? 'bg-[#2a2a2a] text-white' : 'text-gray-500 hover:text-gray-300'}`}>{label}</button>
          ))}
        </div>
        <input
          type="text" value={search} onChange={e => setSearch(e.target.value.toUpperCase())}
          placeholder="Ticker or name…"
          className="bg-[#0f0f0f] border border-[#1a1a1a] rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#2a2a2a] w-40"
        />
        <button onClick={() => fetchFeed(page, feedType)} disabled={loading} className="ml-auto flex items-center gap-1.5 text-xs text-gray-500 hover:text-white px-3 py-1.5 bg-[#0f0f0f] border border-[#1a1a1a] rounded hover:border-[#2a2a2a] transition-colors disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400 text-sm"><AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}</div>}

      {loading && <Skeleton rows={8} />}

      {!loading && (feedType === 'all' || feedType === 'senate') && fSenate.length > 0 && (
        <Section title="Senate" subtitle={`${fSenate.length} trades`} dot="bg-cyan-400" count={fSenate.length} defaultOpen={feedType === 'senate' || feedType === 'all'}>
          {fSenate.map((t, i) => <CongressRow key={i} trade={t} chamber="senate" showTicker={true} onTickerClick={onTickerClick} />)}
        </Section>
      )}

      {!loading && (feedType === 'all' || feedType === 'house') && fHouse.length > 0 && (
        <Section title="House" subtitle={`${fHouse.length} trades`} dot="bg-purple-400" count={fHouse.length} defaultOpen={feedType === 'house'}>
          {fHouse.map((t, i) => <CongressRow key={i} trade={t} chamber="house" showTicker={true} onTickerClick={onTickerClick} />)}
        </Section>
      )}

      {!loading && (feedType === 'all' || feedType === 'insider') && fInsider.length > 0 && (
        <Section title="Insider Form 4" subtitle={`${fInsider.length} trades`} dot="bg-amber-400" count={fInsider.length} defaultOpen={feedType === 'insider'}>
          {fInsider.map((t, i) => <InsiderRow key={i} trade={t} showTicker={true} onTickerClick={onTickerClick} />)}
        </Section>
      )}

      {!loading && fSenate.length === 0 && fHouse.length === 0 && fInsider.length === 0 && (
        <div className="text-center py-12 text-gray-600 text-sm">No results</div>
      )}

      {!loading && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#1a1a1a]">
          <button onClick={() => changePage(Math.max(0, page - 1))} disabled={page === 0} className="px-4 py-2 bg-[#0f0f0f] border border-[#1a1a1a] rounded text-sm text-gray-400 hover:text-white disabled:opacity-30 transition-colors">← Prev</button>
          <span className="text-xs text-gray-600">Page {page + 1}</span>
          <button onClick={() => changePage(page + 1)} className="px-4 py-2 bg-[#0f0f0f] border border-[#1a1a1a] rounded text-sm text-gray-400 hover:text-white transition-colors">Next →</button>
        </div>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// CFO WATCH TAB — market-wide CFO sells
// ══════════════════════════════════════════════════════════════════════════════
function CfoWatch({ session, subscription, onUpgrade, stripeLoading, onTickerClick }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [trades, setTrades]   = useState([]);
  const [scanned, setScanned] = useState(0);
  const [search, setSearch]   = useState('');
  const isPro = ['pro', 'premium', 'founder'].includes(subscription.tier);

  const fetchFeed = useCallback(async () => {
    if (!isPro || !session) return;
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${WORKER_URL}/api/intelligence/cfo-sells?pages=3`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setTrades(d.trades || []);
      setScanned((d.pagesScanned || 0) * 100);
    } catch (e) { setError('Failed to load CFO feed.'); }
    finally { setLoading(false); }
  }, [isPro, session]);

  useEffect(() => { if (isPro) fetchFeed(); }, [isPro, fetchFeed]);

  if (!isPro) return <ProGate onUpgrade={onUpgrade} loading={stripeLoading} />;

  const filtered = trades.filter(t => {
    if (!search) return true;
    const tk = (t.symbol || t.ticker || '').toUpperCase();
    const nm = (t.reportingName || '').toUpperCase();
    return tk.includes(search) || nm.includes(search);
  });

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <p className="text-sm text-white font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-red-400" />CFO sells, market-wide</p>
          <p className="text-xs text-gray-600 mt-0.5">Highest-weight signal. CFO sees the books before anyone else. Scanned {scanned.toLocaleString()} most recent insider records.</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value.toUpperCase())}
            placeholder="Ticker or name…"
            className="bg-[#0f0f0f] border border-[#1a1a1a] rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#2a2a2a] w-40"
          />
          <button onClick={fetchFeed} disabled={loading} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white px-3 py-1.5 bg-[#0f0f0f] border border-[#1a1a1a] rounded hover:border-[#2a2a2a] transition-colors disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4 flex items-center gap-2 text-red-400 text-sm"><AlertTriangle className="w-4 h-4 flex-shrink-0" />{error}</div>}
      {loading && <Skeleton rows={8} />}

      {!loading && filtered.length === 0 && (
        <div className="text-center py-12 text-gray-600 text-sm">No CFO sells in the latest {scanned.toLocaleString()} insider records{search ? ` matching "${search}"` : ''}.</div>
      )}

      {!loading && filtered.length > 0 && (
        <Section title="CFO Sells" subtitle={`${filtered.length} across the market`} dot="bg-red-500" count={filtered.length} defaultOpen={true}>
          {filtered.map((t, i) => <InsiderRow key={i} trade={t} showTicker={true} onTickerClick={onTickerClick} />)}
        </Section>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function Intelligence({ session, subscription, positions, onUpgrade, stripeLoading }) {
  const [tab, setTab] = useState('holdings');
  const [modalTicker, setModalTicker] = useState(null);
  const isPro = ['pro', 'premium', 'founder'].includes(subscription.tier);

  const openTicker = (t) => { if (isPro && t) setModalTicker(t); };

  return (
    <div className="max-w-[1200px] mx-auto p-8">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="w-6 h-6 text-cyan-400" />
        <div>
          <h2 className="text-2xl font-bold text-white">Government Intelligence</h2>
          <p className="text-xs text-gray-500 mt-0.5">Senate · House · Insider Form 4 · Velocity · CFO Watch</p>
        </div>
      </div>
      <div className="flex gap-1 mb-6 bg-[#0f0f0f] border border-[#1a1a1a] rounded-lg p-1 w-fit">
        <button onClick={() => setTab('holdings')} className={`px-5 py-2 rounded text-sm font-semibold transition-colors ${tab === 'holdings' ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:text-gray-300'}`}>My Holdings</button>
        <button onClick={() => setTab('feed')}     className={`px-5 py-2 rounded text-sm font-semibold transition-colors ${tab === 'feed'     ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:text-gray-300'}`}>Market Feed</button>
        <button onClick={() => setTab('cfo')}      className={`px-5 py-2 rounded text-sm font-semibold transition-colors flex items-center gap-1.5 ${tab === 'cfo'      ? 'bg-[#1a1a1a] text-white' : 'text-gray-500 hover:text-gray-300'}`}><AlertTriangle className="w-3.5 h-3.5" />CFO Watch</button>
      </div>
      {tab === 'holdings' && <MyHoldings session={session} subscription={subscription} positions={positions} onUpgrade={onUpgrade} stripeLoading={stripeLoading} onTickerClick={openTicker} />}
      {tab === 'feed'     && <MarketFeed session={session} subscription={subscription} onUpgrade={onUpgrade} stripeLoading={stripeLoading} onTickerClick={openTicker} />}
      {tab === 'cfo'      && <CfoWatch   session={session} subscription={subscription} onUpgrade={onUpgrade} stripeLoading={stripeLoading} onTickerClick={openTicker} />}

      {modalTicker && <TickerModal ticker={modalTicker} session={session} onClose={() => setModalTicker(null)} />}
    </div>
  );
}
